import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';
import { FORM_ERROR } from 'final-form';

import { AppError } from '@vk-hr-tek/core/error';
import { awaitDispatch } from '@vk-hr-tek/core/redux';
import {
  showCustomNotification,
  showNotification,
} from '@vk-hr-tek/core/notifications';

import { CompanyEventsStat, CompanyGroupUsers } from '@app/gen/settings';
import { Entity } from '@app/types';
import {
  AccountRoleModelExceptionsRequest,
  AccountRoleModelExceptionsResponse,
  AccountService,
  DeleteCompanyExceptionsFromAccountRoleModelParams,
  GetAccountRoleModelParams,
  GetAccountRoleModelResponse,
  UpdateAccountRoleModelExceptionsParams,
  UpdateAccountRoleModelParams,
  UpdateAccountRoleModelRequest,
} from '@app/gen/account';
import { ThunkExtra, ThunkValuesAndActions } from '@app/store';

import {
  accountRoleModelExceptionsAdapter,
  SettingsState,
  SettingsWithRootState,
} from '../../settings.state';
import { GetAccountRoleModelExceptionsDto } from '../../../dto';

import { getAccountGroups, getAccounts } from './account.actions';

const WAITING_TIME_AFTER_PUT_NEW_EXCEPTIONS = 250;

const delay = (time: number) =>
  new Promise((resolve) => {
    setTimeout(() => resolve(null), time);
  });

export const getAccountRoleModel = createAsyncThunk<
  GetAccountRoleModelResponse,
  GetAccountRoleModelParams,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getAccountRoleModel',
  async (
    params,
    { rejectWithValue, dispatch, getState, extra: { inject } },
  ) => {
    try {
      const accoutService = inject(AccountService);

      const state = getState();

      if (state.settings.accounts.status === 'idle') {
        await awaitDispatch(dispatch(getAccounts()));
      }

      dispatch(getAccountGroups(params.account_id));

      return await accoutService.getAccountRoleModel({ params });
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateAccountRoleModel = createAsyncThunk<
  void,
  ThunkValuesAndActions<
    UpdateAccountRoleModelParams & UpdateAccountRoleModelRequest
  >,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateAccountRoleModel',
  async (
    { values: { values, account_id }, actions },
    { rejectWithValue, dispatch, getState, extra: { inject } },
  ) => {
    try {
      const accoutService = inject(AccountService);

      const account = getState().settings.accounts.entities[account_id];

      await accoutService.updateAccountRoleModel({
        params: { account_id },
        body: { values },
      });

      actions.resolve(null);

      dispatch(
        showCustomNotification([
          {
            type: 'info',
            title: 'Ролевая модель изменена',
            subtitle: 'Аккаунт',
            message: account?.name ?? '',
          },
        ]),
      );
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: 'Не удалось изменить ролевую модель',
            message: 'Попробуйте еще раз позже',
          },
        ]),
      );
      actions.reject({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getAccountRoleModelExceptions = createAsyncThunk<
  AccountRoleModelExceptionsResponse,
  GetAccountRoleModelExceptionsDto,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getAccountRoleModelExceptions',
  async (
    { accountId, query },
    { rejectWithValue, dispatch, getState, extra: { inject } },
  ) => {
    try {
      const state = getState();

      if (state.settings.accounts.status === 'idle') {
        await awaitDispatch(dispatch(getAccounts()));
      }

      const result = await inject(AccountService).getAccountRoleModelExceptions(
        {
          params: {
            account_id: accountId,
          },
          query,
        },
      );

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateAccountRoleModelExceptions = createAsyncThunk<
  void,
  ThunkValuesAndActions<
    UpdateAccountRoleModelExceptionsParams & AccountRoleModelExceptionsRequest
  >,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateAccountRoleModelExceptions',
  async (
    { values: { company_ids, account_id }, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    const exceptionsCount = company_ids.length;

    try {
      const accoutService = inject(AccountService);

      await accoutService.updateAccountRoleModelExceptions({
        params: { account_id },
        body: { company_ids },
      });

      await delay(WAITING_TIME_AFTER_PUT_NEW_EXCEPTIONS);

      actions.resolve(null);

      dispatch(
        showNotification(`Исключения успешно добавлены (${exceptionsCount})`),
      );
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: `Не удалось добавить исключения (${exceptionsCount})`,
            message: 'Попробуйте еще раз позже',
          },
        ]),
      );

      actions.reject({ [FORM_ERROR]: err });

      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const deleteAccountRoleModelException = createAsyncThunk<
  void,
  DeleteCompanyExceptionsFromAccountRoleModelParams,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/deleteAccountRoleModelException',
  async (
    params,
    { rejectWithValue, dispatch, getState, extra: { inject } },
  ) => {
    try {
      const state = getState();

      await inject(AccountService).deleteCompanyExceptionsFromAccountRoleModel({
        params,
      });

      const account =
        state.settings.accountRoleModelExceptions.entities[params.company_id];

      dispatch(
        showCustomNotification([
          {
            type: 'info',
            title: 'Компания успешно удалена из списка исключений',
            message: account?.name ?? '',
          },
        ]),
      );
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: 'Не удалось удалить компанию из списка исключений',
            message: 'Попробуйте еще раз позже',
          },
        ]),
      );

      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getAddAccountRoleModelExceptionsInfo = createAsyncThunk<
  void,
  string,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getAddAccountRoleModelExceptionsInfo',
  async (accountId, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();

      await Promise.all([
        state.settings.accounts.status === 'idle' &&
          awaitDispatch(dispatch(getAccounts())),
        awaitDispatch(
          dispatch(
            getAccountRoleModelExceptions({
              accountId: accountId,
            }),
          ),
        ),
      ]);
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsAccountRoleModelReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getAccountRoleModel.pending, (state) => {
    state.accountRoleModel.status = 'loading';
    state.accountRoleModel.error = null;
  });
  builder.addCase(getAccountRoleModel.fulfilled, (state, { payload }) => {
    state.accountRoleModel.status = 'complete';
    state.accountRoleModel.roleModelEnabled = payload.role_model_enabled;
    state.accountRoleModel.domains = payload.domains || [];
    state.accountRoleModel.error = null;
  });
  builder.addCase(getAccountRoleModel.rejected, (state, { payload, error }) => {
    state.accountRoleModel.status = 'failed';
    state.accountRoleModel.error =
      payload ||
      ({
        info: (error && error.message) || 'Unknown error',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });

  builder.addCase(getAccountRoleModelExceptions.pending, (state) => {
    state.accountRoleModelExceptions.status = 'loading';
    state.accountRoleModelExceptions.error = null;
  });
  builder.addCase(
    getAccountRoleModelExceptions.fulfilled,
    (state, { payload }) => {
      if (payload) {
        accountRoleModelExceptionsAdapter.upsertMany(
          state.accountRoleModelExceptions,
          payload.companies,
        );
        state.accountRoleModelExceptions.currentIds = payload.companies.map(
          ({ id }) => id,
        );
      }

      state.accountRoleModelExceptions.total = payload.total_count;
      state.accountRoleModelExceptions.status = 'complete';
      state.accountRoleModelExceptions.error = null;
    },
  );
  builder.addCase(
    getAccountRoleModelExceptions.rejected,
    (state, { payload, error }) => {
      state.accountRoleModelExceptions.status = 'failed';
      state.accountRoleModelExceptions.error =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
    },
  );

  builder.addCase(
    deleteAccountRoleModelException.fulfilled,
    (state, { meta }) => {
      accountRoleModelExceptionsAdapter.removeOne(
        state.accountRoleModelExceptions,
        meta.arg.company_id,
      );
      state.accountRoleModelExceptions.total =
        state.accountRoleModelExceptions.total - 1;
    },
  );

  builder.addCase(getAddAccountRoleModelExceptionsInfo.pending, (state) => {
    state.addAccountRoleModelExceptions.status = 'loading';
    state.addAccountRoleModelExceptions.error = null;
  });
  builder.addCase(getAddAccountRoleModelExceptionsInfo.fulfilled, (state) => {
    state.addAccountRoleModelExceptions.status = 'complete';
    state.addAccountRoleModelExceptions.error = null;
  });
  builder.addCase(
    getAddAccountRoleModelExceptionsInfo.rejected,
    (state, { payload, error }) => {
      state.addAccountRoleModelExceptions.status = 'failed';
      state.addAccountRoleModelExceptions.error =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
    },
  );
};
